import React, { forwardRef } from 'react';

import type { TextInput as RNTextInput } from 'react-native';

import { TextInput } from '../inputs';
import type { TextInputProps } from '../inputs';

export interface NameInputProps extends Omit<
  TextInputProps,
  'type' | 'ariaLabelledBy' | 'ariaLabel'
> {
  /**
   * Analytics name for tracking events
   */
  analyticsName: string;
  /**
   * Error message to display for invalid name (e.g., empty or whitespace only)
   * @default "Please enter your full name."
   */
  invalidNameError?: string;
  /**
   * ARIA labelledBy attribute
   */
  ariaLabelledBy?: string;
}

/**
 * NameInput is a specialized name input component with validation.
 * It extends TextInput with name-specific configuration.
 *
 * Features:
 * - Name autocomplete support
 * - Validation for required and trimmed names
 * - Consistent styling
 *
 * @example
 * ```tsx
 * <NameInput
 *   analyticsName="my-app-registration"
 *   title="Full name"
 *   onChangeText={setName}
 *   required={true}
 * />
 * ```
 */
export const NameInput = forwardRef<RNTextInput, NameInputProps>(
  (
    {
      analyticsName,
      invalidNameError = 'Please enter your full name.',
      title = 'Full name',
      size = 'medium',
      autoComplete = 'name',
      ariaLabelledBy,
      ...props
    },
    ref,
  ) => {
    return (
      <TextInput
        ref={ref}
        id="name"
        analyticsName={`${analyticsName}-name`}
        size={size}
        autoComplete={autoComplete}
        errorText={props.errorText || invalidNameError}
        {...(ariaLabelledBy ? { ariaLabelledBy } : { title })}
        {...props}
      />
    );
  },
);

NameInput.displayName = 'NameInput';
